import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layout } from "../../components/Layout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Search, Loader2, ImageOff, X } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { fetchProductsAPI, fetchProductTypesAPI } from "./inventoryAPI";
import { clearError, clearProducts } from "./inventorySlice";

function Inventory({ onNavigate }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const scrollRef = useRef(null);

  const { productsList, page, hasMore, totalCount, loading, error } =
    useSelector((state) => state.inventory || {});
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  const loadProducts = useCallback(
    (reset = false, query = "") => {
      const pageToFetch = reset ? 1 : page + 1;

      if (loading || (loadingMore && !reset)) return;
      if (!hasMore && !reset) return;

      const requestData = {
        pageNo: pageToFetch,
        pageSize: 10,
        searchQuery: query || null,
        companyFilter: 1,
      };

      if (!reset) setLoadingMore(true);

      dispatch(fetchProductsAPI(requestData, !reset)).finally(() => {
        if (!reset) setLoadingMore(false);
      });
    },
    [dispatch, page, hasMore, loading, loadingMore]
  );

  useEffect(() => {
    dispatch(clearProducts());
    const debouncedLoad = setTimeout(() => {
      loadProducts(true, searchQuery);
    }, 500);

    return () => clearTimeout(debouncedLoad);
  }, [searchQuery, dispatch]);

  useEffect(() => {
    dispatch(fetchProductTypesAPI());
  }, [dispatch]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      loadProducts(false, searchQuery);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const SkeletonLoader = ({ count = 4 }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="w-full h-48 bg-gray-200 animate-pulse rounded-md"></div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <Layout
      title={
        <FormattedMessage
          id="inventory.title"
          defaultMessage="Inventory Management"
        />
      }
      onNavigate={onNavigate}
    >
      <div className="h-full flex flex-col">
        {/* Search bar */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 pb-2 mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              placeholder={intl.formatMessage({
                id: "inventory.searchPlaceholder",
                defaultMessage: "Search by product name...",
              })}
              value={searchQuery}
              onChange={handleSearchChange}
              className="!mt-0 pl-10 pr-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full"
                onClick={clearSearch}
              >
                <X className="h-4 w-4 text-gray-500" />
              </Button>
            )}
          </div>
        </div>

        {/* Product List */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto flex-grow"
          style={{ maxHeight: "calc(100vh - 180px)" }}
        >
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-center">
              <p className="text-red-800 font-medium">
                {typeof error === "string" ? (
                  error
                ) : (
                  <FormattedMessage
                    id="inventory.error"
                    defaultMessage="An unexpected error occurred."
                  />
                )}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => dispatch(clearError())}
                className="mt-2 text-red-700"
              >
                <FormattedMessage
                  id="inventory.dismiss"
                  defaultMessage="Dismiss"
                />
              </Button>
            </div>
          )}

          {loading && productsList.length === 0 ? (
            <SkeletonLoader count={8} />
          ) : !loading && productsList.length === 0 && !error ? (
            <p className="text-center py-10 text-gray-500">
              <FormattedMessage
                id="inventory.noProducts"
                defaultMessage="No products found."
              />
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {productsList.map((product) => (
                  <Card key={product.id} className="overflow-hidden">
                    <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                      {product.imageBase64 ? (
                        <img
                          src={`data:image/png;base64,${product.imageBase64}`}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageOff className="h-10 w-10 text-gray-400" />
                      )}
                    </div>
                    <CardContent className="!p-4">
                      <h3
                        className="font-semibold truncate"
                        title={product.name}
                      >
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {product.description || (
                          <FormattedMessage
                            id="inventory.noDescription"
                            defaultMessage="No Description"
                          />
                        )}
                      </p>
                      <p className="font-bold text-lg mt-2">
                        ${product.salePrice || 0}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {loadingMore && <SkeletonLoader count={4} />}
              {!hasMore && productsList.length > 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <FormattedMessage
                    id="inventory.totalProducts"
                    defaultMessage="Total: {count} products"
                    values={{ count: totalCount }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Inventory;
